import {Component, Input, Output, EventEmitter, SimpleChanges, OnChanges} from '@angular/core';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {UtilService} from '../../../../../../../common/services/util.service';
import {ApplicationEmploymentDTO} from '../../../../../../models/application/ApplicationEmploymentDTO';
import {EmploymentDetailsDialogComponent} from '../../../../../dialogs/employment-details-dialog/employment-details-dialog.component';
import {ConfirmationDialogComponent} from '../../../../../../../common/dialogs/confirmation-dialog/confirmation-dialog.component';
import {UserAuthorisationService} from '../../../../../../../common/services/user-authorisation.service';

@Component({
  selector: 'app-person-employment-details',
  templateUrl: './person-employment-details.component.html',
  styleUrls: ['./person-employment-details.component.scss']
})
export class PersonEmploymentDetailsComponent implements OnChanges {
  @Input() employmentDetails: ApplicationEmploymentDTO[];
  @Input() applicationPersonId: number;
  @Input() isApplicationLocked: boolean;
  @Output() employmentUpdated = new EventEmitter<{
    applicationPersonId: number;
    employment: ApplicationEmploymentDTO;
    isEdit: boolean;
    isDelete?: boolean;
  }>();

  hasPermissions = false;

  constructor(
    private dialog: MatDialog,
    private utilService: UtilService,
    private userAuthorisationService: UserAuthorisationService
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isApplicationLocked) {
      this.isApplicationLocked = changes.isApplicationLocked.currentValue;
      this.setPermission();
    }
  }

  setPermission(): void {
    const hasRole = this.userAuthorisationService.hasOnBoardingQC() || this.userAuthorisationService.hasRoleEnterpriseManager();
    this.hasPermissions = hasRole && !this.isApplicationLocked;
  }

  getEmploymentDetailsString(employment: ApplicationEmploymentDTO): string {
    let result = `Income: ${this.formatValue(employment.incomeAmount)}`;
    if (employment.incomeBracketCategoryCode) {
      result += ` (category code: ${employment.incomeBracketCategoryCode})`;
    }
    if (employment.employmentNumber) {
      result += ` Employee no.: ${employment.employmentNumber} -`;
    }
    result += ` Period: ${this.formatDate(employment.startDate)} - ${employment.endDate != null ? this.formatDate(employment.endDate) : 'current'}`;
    return result;
  }

  onAddEmployment(): void {
    const dialogRef: MatDialogRef<EmploymentDetailsDialogComponent> = this.dialog.open(EmploymentDetailsDialogComponent, {
      width: '40vw',
      data: {
        applicationPersonId: this.applicationPersonId
      }
    });

    dialogRef.afterClosed().subscribe((newEmployment: ApplicationEmploymentDTO) => {
      if (newEmployment) {
        this.employmentUpdated.emit({
          applicationPersonId: this.applicationPersonId,
          employment: newEmployment,
          isEdit: false
        });
      }
    });
  }

  onEditEmployment(employment: ApplicationEmploymentDTO): void {
    const dialogRef: MatDialogRef<EmploymentDetailsDialogComponent> = this.dialog.open(EmploymentDetailsDialogComponent, {
      width: '40vw',
      data: {
        applicationPersonId: this.applicationPersonId,
        employment
      }
    });

    dialogRef.afterClosed().subscribe((updatedEmployment: ApplicationEmploymentDTO) => {
      if (updatedEmployment) {
        this.employmentUpdated.emit({
          applicationPersonId: this.applicationPersonId,
          employment: updatedEmployment,
          isEdit: true
        });
      }
    });
  }

  onDeleteEmployment(employmentToDelete: ApplicationEmploymentDTO): void {
    const confirmationMessage = `Are you sure you want to delete this ${this.getEmploymentDetailsString(employmentToDelete)} employment details?`;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '40vw',
      data: confirmationMessage
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.employmentUpdated.emit({
          applicationPersonId: this.applicationPersonId,
          employment: employmentToDelete,
          isEdit: false,
          isDelete: true
        });
      }
    });
  }

  private formatDate(date: Date | string): string {
    return date ? this.utilService.dateFormat(date) : 'Unknown';
  }

  private formatValue(value: number): string {
    return value ? value.toFixed(2) : '0.00';
  }
}
